// WebSocket hook with ABSOLUTE guard
// NO WebSocket will be created unless REACT_APP_ENABLE_WEBSOCKETS === 'true'

const WEBSOCKETS_ENABLED = process.env.REACT_APP_ENABLE_WEBSOCKETS === 'true';

// Disabled return object - no side effects, no hooks, no instantiation
const DISABLED_RETURN = {
  isConnected: false,
  lastMessage: null,
  sendMessage: () => false,
  addMessageHandler: () => () => {},
  connect: () => {},
  disconnect: () => {},
  enabled: false
};

// If WebSockets are disabled, export a no-op hook immediately
// This prevents ANY WebSocket-related code from running
export const useWebSocket = WEBSOCKETS_ENABLED ? useWebSocketEnabled : useWebSocketDisabled;

// Disabled version - absolutely no side effects
function useWebSocketDisabled() {
  return DISABLED_RETURN;
}

// Only import React hooks if WebSockets are enabled
// This function is ONLY called when WEBSOCKETS_ENABLED === true
function useWebSocketEnabled(userId) {
  // Dynamic import of React hooks only when needed
  const { useState, useEffect, useRef, useCallback } = require('react');
  
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const messageHandlersRef = useRef([]);
  const userIdRef = useRef(userId);

  // Get backend URL
  const getBackendUrl = () => {
    const envUrl = process.env.REACT_APP_BACKEND_URL;
    if (envUrl && envUrl.trim() !== '') {
      return envUrl;
    }
    if (typeof window !== 'undefined') {
      return window.location.origin;
    }
    return '';
  };

  const BACKEND_URL = getBackendUrl();

  // Construct WebSocket URL
  const getWebSocketURL = () => {
    if (!BACKEND_URL) return null;
    const cleanUrl = BACKEND_URL.replace(/\/$/, '');
    if (cleanUrl.startsWith('https://')) {
      return cleanUrl.replace('https://', 'wss://');
    } else if (cleanUrl.startsWith('http://')) {
      return cleanUrl.replace('http://', 'ws://');
    }
    return null;
  };

  const WS_URL = getWebSocketURL();

  // Keep userId ref updated
  useEffect(() => {
    userIdRef.current = userId;
  }, [userId]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  const connect = useCallback(() => {
    if (!WS_URL) return;
    
    const currentUserId = userIdRef.current;
    if (!currentUserId || typeof currentUserId !== 'string') return;
    if (wsRef.current) return;

    try {
      const ws = new WebSocket(`${WS_URL}/ws/${currentUserId}`);
      
      ws.onopen = () => {
        setIsConnected(true);
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          setLastMessage(data);
          messageHandlersRef.current.forEach(handler => handler(data));
        } catch (error) {
          // Silent fail
        }
      };

      ws.onerror = () => {
        // Silent error - no console spam
      };

      ws.onclose = () => {
        setIsConnected(false);
        wsRef.current = null;
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }
        // NO auto-reconnect - prevents spam
      };

      wsRef.current = ws;
    } catch (error) {
      // Silent fail
    }
  }, [WS_URL]);

  const sendMessage = useCallback((message) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
      return true;
    }
    return false;
  }, []);

  const addMessageHandler = useCallback((handler) => {
    messageHandlersRef.current = [...messageHandlersRef.current, handler];
    return () => {
      messageHandlersRef.current = messageHandlersRef.current.filter(h => h !== handler);
    };
  }, []);

  // Connect on mount
  useEffect(() => {
    if (userId && WS_URL) {
      connect();
    }
    return () => {
      disconnect();
    };
  }, [userId, connect, disconnect, WS_URL]);

  return {
    isConnected,
    lastMessage,
    sendMessage,
    addMessageHandler,
    connect,
    disconnect,
    enabled: true
  };
}
